import { useEffect, useRef, useState, useCallback } from 'react'
import { Terminal as TerminalIcon, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import { ClipboardAddon } from '@xterm/addon-clipboard'
import { SearchAddon } from '@xterm/addon-search'
import '@xterm/xterm/css/xterm.css'
import Panel from './Panel'
import TerminalSearchBar from './TerminalSearchBar'
import { useSettings } from '../../hooks/useSettings'
import { createTerminalOptions, getBackgroundColor, getTerminalTheme, mapCursorStyle, setupBellHandler, loadWebGLAddon } from '../../lib/terminalConfig'
import { resolveApiBase, withMountBase } from '../../lib/mountBase'
import { recordClientTelemetry } from '../../lib/clientTelemetry'
import { startMomentum, type MomentumState } from '../../lib/touchMomentum'

// Progressive connection states for better UX
type ConnectionStatus =
  | 'checking'      // Querying backend status
  | 'starting'      // Container starting
  | 'initializing'  // Waiting for terminal service
  | 'connecting'    // WebSocket connecting
  | 'reconnecting'  // Recovering from a dropped/degraded connection
  | 'degraded'      // Socket errored and reconnect is scheduled
  | 'connected'
  | 'error'

const statusMessages: Record<ConnectionStatus, string> = {
  checking: 'Checking workspace status...',
  starting: 'Starting workspace...',
  initializing: 'Initializing terminal service...',
  connecting: 'Connecting...',
  reconnecting: 'Recovering connection...',
  degraded: 'Connection degraded...',
  connected: 'Connected',
  error: 'Connection failed',
}

interface TerminalPanelProps {
  isMaximized?: boolean
  onMaximize?: () => void
  onClose?: () => void
  closeable?: boolean
  cwd?: string  // Initial working directory (used to determine app name for per-app sessions)
  appName?: string  // App name for per-app workspace containers (takes precedence over cwd extraction)
  tmuxSession?: string  // Specific tmux session to attach to (e.g., "main", "fix-login-bug")
  onSendDataRef?: React.MutableRefObject<((data: string) => void) | null>  // Ref for external data injection (e.g., MobileKeyboardBar)
}

interface ReconnectOptions {
  startupRetry?: boolean
  fromConnectedDrop?: boolean
  fromDegradedState?: boolean
}

/**
 * Extract app name from cwd path for per-app tmux sessions.
 * Supports formats:
 *   - /home/coder/workspace/apps/{name}
 *   - workspace/apps/{name}
 *   - /workspace/apps/{name}
 */
function extractAppName(cwd?: string): string | null {
  if (!cwd) return null

  // Match patterns like /home/coder/workspace/apps/{name} or workspace/apps/{name}
  const match = cwd.match(/(?:\/home\/coder\/)?workspace\/apps\/([^/]+)/)
  return match ? match[1] : null
}

export default function TerminalPanel({
  isMaximized,
  onMaximize,
  onClose,
  closeable = true,
  cwd,
  appName: propAppName,
  tmuxSession,
  onSendDataRef,
}: TerminalPanelProps) {
  const terminalRef = useRef<HTMLDivElement>(null)
  const terminalInstance = useRef<Terminal | null>(null)
  const fitAddon = useRef<FitAddon | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const [status, setStatus] = useState<ConnectionStatus>('checking')
  const statusRef = useRef(status)
  const [showSearch, setShowSearch] = useState(false)
  const showSearchRef = useRef(false)
  const searchAddonRef = useRef<SearchAddon | null>(null)
  // Retained for potential future overlay; value is currently unread since
  // the previous "Scrolled up — tap to return" button was removed in favor of
  // natural scroll-down auto-exit from copy-mode.
  const [, setIsScrolledUp] = useState(false)
  const exitCopyModeRef = useRef<(() => void) | null>(null)
  const autoReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const startupRetryAttemptsRef = useRef(0)
  const connectStartedAtRef = useRef<number>(Date.now())
  const isPanelActiveRef = useRef(false)
  const lastInboundMessageAtRef = useRef<number>(Date.now())
  const lastOutboundMessageAtRef = useRef<number>(Date.now())
  const hiddenSinceRef = useRef<number | null>(null)
  const lastVisibilityRecoverAtRef = useRef<number>(0)
  const highBufferCountRef = useRef(0)
  const requestRecoverRef = useRef<((fromDegraded?: boolean) => void) | null>(null)
  const MAX_AUTO_RECONNECT_ATTEMPTS = 8
  const MAX_STARTUP_RETRY_ATTEMPTS = 24
  const WS_HEALTH_INTERVAL_MS = 5000
  const WS_KEEPALIVE_INTERVAL_MS = 20000
  const WS_HIGH_BUFFER_BYTES = 128 * 1024
  const WS_HIGH_BUFFER_STRIKES = 3
  const VISIBILITY_RECOVER_AFTER_MS = 90000
  const VISIBILITY_RECOVER_COOLDOWN_MS = 5000

  // Use prop appName if provided, otherwise extract from cwd
  const appName = propAppName || extractAppName(cwd)
  const apiBase = resolveApiBase(typeof window !== 'undefined' ? window.location.pathname : '/')
  const reportTerminalTelemetry = useCallback((
    eventType: string,
    message: string,
    details?: Record<string, unknown>,
    severity: 'info' | 'warning' | 'error' = 'warning'
  ) => {
    recordClientTelemetry({
      eventType,
      panel: 'terminal',
      severity,
      appName: appName || undefined,
      message,
      details: {
        tmux_session: tmuxSession || 'main',
        ...details,
      },
    })
  }, [appName, tmuxSession])

  const buildTerminalWebSocketUrl = useCallback((): string => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsPath = appName
      ? withMountBase(`/workspace/app/${encodeURIComponent(appName)}/terminal/ws`, window.location.pathname)
      : withMountBase('/terminal/ws', window.location.pathname)
    const sessionArg = appName && tmuxSession ? `?arg=${encodeURIComponent(tmuxSession)}` : ''
    return `${protocol}//${window.location.host}${wsPath}${sessionArg}`
  }, [appName, tmuxSession])

  const markInboundActivity = useCallback(() => {
    lastInboundMessageAtRef.current = Date.now()
    highBufferCountRef.current = 0
  }, [])

  const markOutboundActivity = useCallback(() => {
    lastOutboundMessageAtRef.current = Date.now()
  }, [])

  const buildResizeFrame = useCallback((cols: number, rows: number) => {
    const msg = JSON.stringify({ columns: cols, rows: rows })
    const data = new Uint8Array(msg.length + 1)
    data[0] = 49 // '1' = resize type
    for (let i = 0; i < msg.length; i++) {
      data[i + 1] = msg.charCodeAt(i)
    }
    return data
  }, [])

  // Expose sendData function for external keyboard bar (e.g., MobileKeyboardBar via TerminalTabs)
  useEffect(() => {
    if (onSendDataRef) {
      onSendDataRef.current = (data: string) => {
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) {
          const encoded = new TextEncoder().encode(data)
          const msg = new Uint8Array(encoded.length + 1)
          msg[0] = 48 // '0' = input type
          msg.set(encoded, 1)
          ws.send(msg)
          markOutboundActivity()
        } else {
          if (statusRef.current === 'connected' || statusRef.current === 'degraded') {
            setStatus('reconnecting')
          }
          requestRecoverRef.current?.(true)
        }
        terminalInstance.current?.focus()
      }
    }
    return () => {
      if (onSendDataRef) {
        onSendDataRef.current = null
      }
    }
  }, [markOutboundActivity, onSendDataRef])

  // Poll backend status until services are ready
  const waitForServicesReady = useCallback(async (): Promise<boolean> => {
    if (!appName) {
      // Legacy mode without app name - skip polling
      setStatus('connecting')
      return true
    }

    const maxAttempts = 10
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    setStatus('checking')
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${apiBase}/workspace/app/${encodeURIComponent(appName)}/status`)
        if (response.ok) {
          const data = await response.json()
          if (data.container === 'running' && data.terminal_ready) {
            setStatus('connecting')
            return true
          }
          if (data.container === 'running') {
            setStatus('initializing')
          } else if (data.container === 'not_found') {
            // Connecting to terminal endpoint will trigger container start when missing.
            setStatus('starting')
            return true
          } else {
            setStatus('starting')
          }
        }
      } catch {
        setStatus('starting')
      }
      await sleep(400)
    }

    // Proceed anyway and rely on startup retry loop below if the service
    // still isn't ready yet.
    setStatus('connecting')
    return true
  }, [apiBase, appName])

  // Get terminal settings
  const { settings } = useSettings()
  const fontSize = settings?.terminal_font_size ?? (settings?.terminal as Record<string, unknown>)?.font_size as number ?? 14
  const terminalTheme = (settings?.terminal as Record<string, unknown>)?.theme as string ?? 'dark'
  const scrollbackLines = (settings?.terminal as Record<string, unknown>)?.scrollback_lines as number ?? 10000
  const cursorStyle = (settings?.terminal as Record<string, unknown>)?.cursor_style as string ?? 'block'

  // Keep refs in sync with state so async code always reads latest values
  const fontSizeRef = useRef(fontSize)
  const terminalThemeRef = useRef(terminalTheme)
  const scrollbackLinesRef = useRef(scrollbackLines)
  const cursorStyleRef = useRef(cursorStyle)

  useEffect(() => { fontSizeRef.current = fontSize }, [fontSize])
  useEffect(() => { terminalThemeRef.current = terminalTheme }, [terminalTheme])
  useEffect(() => { scrollbackLinesRef.current = scrollbackLines }, [scrollbackLines])
  useEffect(() => { cursorStyleRef.current = cursorStyle }, [cursorStyle])

  useEffect(() => {
    statusRef.current = status
  }, [status])

  useEffect(() => {
    showSearchRef.current = showSearch
  }, [showSearch])

  useEffect(() => {
    if (!terminalRef.current) return

    isPanelActiveRef.current = true
    let cancelled = false
    // Track addon disposables for cleanup (set inside async init, used in cleanup)
    let webglDispose: (() => void) | null = null
    let bellDispose: (() => void) | null = null
    let touchCleanup: (() => void) | null = null

    const initializeTerminal = async () => {
      // Wait for backend services to be ready before connecting
      const ready = await waitForServicesReady()
      if (cancelled || !ready) {
        if (!cancelled) setStatus('error')
        return
      }

      if (!terminalRef.current) return

      // Create terminal with shared config
      const terminal = new Terminal(createTerminalOptions({
        fontSize,
        theme: terminalTheme,
        scrollbackLines,
        cursorStyle,
      }))

    terminalInstance.current = terminal

    // Add addons
    const fit = new FitAddon()
    fitAddon.current = fit
    terminal.loadAddon(fit)
    terminal.loadAddon(new WebLinksAddon())
    // ClipboardAddon handles OSC 52 sequences from tmux for copy-to-clipboard
    terminal.loadAddon(new ClipboardAddon())

    // Search addon for Cmd+F / Ctrl+F search overlay
    const search = new SearchAddon()
    terminal.loadAddon(search)
    searchAddonRef.current = search

    // Open terminal
    terminal.open(terminalRef.current)
    fit.fit()

    // Load WebGL renderer (falls back to DOM on context loss or missing support)
    // Must load BEFORE applying theme so WebGL picks up the correct colors
    const webgl = loadWebGLAddon(terminal)
    webglDispose = webgl.dispose

    // Apply latest settings from refs (settings may have loaded after
    // the effect closure captured initial defaults, causing a race condition)
    terminal.options.fontSize = fontSizeRef.current
    terminal.options.theme = getTerminalTheme(terminalThemeRef.current)
    terminal.options.scrollback = scrollbackLinesRef.current
    terminal.options.cursorStyle = mapCursorStyle(cursorStyleRef.current)
    // Clear cached glyph textures so WebGL re-renders with updated theme colors
    terminal.clearTextureAtlas()
    // Force xterm viewport background (CSS default is #000, WebGL doesn't override it)
    const vp = terminalRef.current?.querySelector('.xterm-viewport') as HTMLElement | null
    if (vp) {
      vp.style.backgroundColor = getTerminalTheme(terminalThemeRef.current).background ?? ''
    }

    // Register bell handler for tab title flash notification
    const bellDisposable = setupBellHandler(terminal)
    bellDispose = () => bellDisposable.dispose()

    // Intercept keyboard shortcuts:
    // - Cmd+F / Ctrl+F: search bar instead of browser search
    // - Ctrl+V: let browser handle paste (terminals send literal ^V / \x16)
    // - Ctrl+digit: pass through for tab switching (handled by document listener in TerminalTabs)
    terminal.attachCustomKeyEventHandler((event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'f' && event.type === 'keydown') {
        setShowSearch(true)
        return false
      }
      if (event.key === 'Escape' && event.type === 'keydown' && showSearchRef.current) {
        setShowSearch(false)
        terminal.focus()
        return false
      }
      // Ctrl+V → let browser handle paste (Cmd+V on Mac already works natively)
      if (event.ctrlKey && !event.metaKey && !event.shiftKey && event.key === 'v' && event.type === 'keydown') {
        return false
      }
      // Pass Ctrl+digit (1-9) through to document handler for tab switching
      if (event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        const digit = parseInt(event.key, 10)
        if (digit >= 1 && digit <= 9) {
          return false
        }
      }
      return true
    })

    // =========================================================================
    // Wheel-to-scroll bridge: tmux mouse is OFF so xterm.js handles selection
    // natively. But xterm.js has no scrollback in alternate screen mode (tmux).
    // We intercept wheel events and send tmux copy-mode key sequences through
    // the WebSocket.
    //
    // Key design decisions:
    // - Use Ctrl+U/Ctrl+D (half-page up/down) for fast viewport scrolling
    //   instead of line-by-line k/j keys which are painfully slow
    // - Separate copy-mode entry (prefix+[) from scroll keys with a delay
    //   so tmux has time to switch key tables
    // - Do NOT exit copy-mode on click -- user needs to click-drag to select
    //   text from scrollback. Copy-mode exits on: keyboard input (onData),
    //   scrolling back to bottom, or pressing Escape/q
    // =========================================================================
    let scrolledUp = false
    let scrollOffset = 0
    let pendingScrollDelta = 0 // Accumulated pixel delta for scroll batching
    let scrollTimer: ReturnType<typeof setTimeout> | null = null
    let copyModeReady = false // True once tmux has entered copy-mode
    // Mouse wheel and touch want different scroll granularities. Wheel maps
    // each ~200px of accumulated delta to one tmux half-page (~20 lines), the
    // way scrollback feels in any other terminal. Touch maps each ~lineHeight
    // pixels to one line so finger drag is finger-proportional. Both share
    // the same buffer + flush — this flag tells the flush which keys to send.
    let scrollSource: 'wheel' | 'touch' = 'wheel'

    const sendToWs = (data: string) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      const encoded = new TextEncoder().encode(data)
      const msg = new Uint8Array(encoded.length + 1)
      msg[0] = 48 // '0' = input type
      msg.set(encoded, 1)
      ws.send(msg)
      markOutboundActivity()
    }

    // Measure the on-screen line height from the xterm viewport so we can
    // translate pixel deltas to copy-mode line jumps. Falls back to 20px if
    // the viewport hasn't been measured yet (e.g. very first frame).
    const getLineHeight = (): number => {
      const vp = terminalRef.current?.querySelector('.xterm-viewport') as HTMLElement | null
      const h = vp?.clientHeight ?? 0
      const rows = terminal.rows || 1
      return h >= rows * 10 ? h / rows : 20
    }

    const flushScrollKeys = () => {
      scrollTimer = null
      if (pendingScrollDelta === 0) return
      const delta = pendingScrollDelta
      pendingScrollDelta = 0

      if (scrollSource === 'wheel') {
        // Desktop wheel: half-page jumps (Ctrl-U up / Ctrl-D down in tmux
        // copy-mode vi). ~200px accumulated wheel → 1 half-page (~20 lines),
        // matching the feel of every other terminal/scrollable view. Without
        // this, line-by-line emission produces a 4× slower scroll than main.
        const halfPages = Math.max(1, Math.round(Math.abs(delta) / 200))
        const key = delta > 0 ? '\x15' : '\x04'
        sendToWs(key.repeat(halfPages))
        return
      }

      // Mobile touch: line-by-line so a finger drag of N pixels reveals
      // exactly ⌈N/lineHeight⌉ lines. With cursor-arrows (\x1b[A / \x1b[B)
      // the cursor first traversed the viewport before tmux scrolled, so
      // we use vi-mode SCROLL keys (Ctrl-Y / Ctrl-E) which move the
      // viewport itself one line per keystroke.
      const lh = getLineHeight()
      const lines = Math.max(1, Math.round(Math.abs(delta) / lh))
      const seq = delta > 0 ? '\x19' : '\x05'
      sendToWs(seq.repeat(lines))
    }

    // Register custom wheel handler INSIDE xterm.js to intercept wheel events
    // before xterm's internal handlers call stopPropagation().
    //
    // Why this is necessary: xterm.js has two internal wheel listeners that fire
    // before events bubble to our wrapper div:
    // 1. SmoothScrollableElement on .xterm-scrollable-element (viewport scrollbar)
    // 2. CoreBrowserTerminal.bindMouse on .xterm.terminal (alt screen handling)
    // In the tmux case (alternate screen, no scrollback), handler #2 converts
    // wheel events to cursor escape sequences and calls cancel(ev, true) which
    // calls ev.stopPropagation(), preventing events from ever reaching our div.
    //
    // attachCustomWheelEventHandler runs BEFORE both internal handlers.
    // Returning false prevents xterm from processing the event at all.
    terminal.attachCustomWheelEventHandler((e: WheelEvent) => {
      // Let modifier+scroll through to xterm/browser (browser zoom, etc.)
      if (e.ctrlKey || e.metaKey) return true
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return true

      e.preventDefault()
      scrollSource = 'wheel'

      if (e.deltaY < 0) {
        // Scroll up
        if (!scrolledUp) {
          // Enter copy-mode first, then send scroll keys after a delay
          sendToWs('\x02[') // Ctrl+B + [ = enter copy-mode
          scrolledUp = true
          copyModeReady = false
          pendingScrollDelta += Math.abs(e.deltaY)
          scrollOffset += Math.abs(e.deltaY)
          setIsScrolledUp(true)
          // Delay to let tmux switch to copy-mode key table
          if (scrollTimer) clearTimeout(scrollTimer)
          scrollTimer = setTimeout(() => {
            copyModeReady = true
            flushScrollKeys()
          }, 80)
          return false
        }
        pendingScrollDelta += Math.abs(e.deltaY)
        scrollOffset += Math.abs(e.deltaY)
      } else if (scrolledUp) {
        // Scroll down
        const absDelta = Math.abs(e.deltaY)
        pendingScrollDelta -= absDelta
        scrollOffset = Math.max(0, scrollOffset - absDelta)
        // Use a small threshold (50px ~ one small scroll tick) so we don't
        // require pixel-perfect return. When near 0, just exit immediately.
        if (scrollOffset <= 50) {
          // Flush any pending down-scroll first, then exit copy-mode
          if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
          if (pendingScrollDelta !== 0) flushScrollKeys()
          sendToWs('q') // Exit copy-mode
          scrolledUp = false
          copyModeReady = false
          scrollOffset = 0
          pendingScrollDelta = 0
          setIsScrolledUp(false)
          return false
        }
      } else {
        // Scroll down but not in copy-mode - let xterm handle it
        return true
      }

      // Debounce scroll key sends for smooth scrolling
      // Use longer debounce if copy-mode hasn't been confirmed ready yet
      if (scrollTimer) clearTimeout(scrollTimer)
      // 16ms = ~one frame at 60Hz, ~two frames at 120Hz (ProMotion). Tighter
      // gate keeps scroll keys flowing in step with the display refresh, which
      // is what makes the gesture feel "tracked" on newer iPhones. The 80ms
      // pre-warmup gate is preserved while tmux confirms its copy-mode switch.
      scrollTimer = setTimeout(flushScrollKeys, copyModeReady ? 16 : 80)
      return false
    })

    // =========================================================================
    // Touch-to-scroll bridge: translates one-finger swipe gestures into tmux
    // copy-mode scroll commands, sharing state with the wheel handler above.
    // Momentum (inertial scrolling) continues after finger lift via rAF loop.
    // =========================================================================
    // =====================================================================
    // TOUCH SIGN CONVENTION (intentionally OPPOSITE of the wheel handler):
    //
    //   wheel paradigm (lines 433-490): e.deltaY < 0  =>  view earlier output
    //   touch paradigm (this block):    deltaY < 0    =>  view earlier output
    //                                   where deltaY = lastTouchY - currentY
    //                                   so deltaY < 0  <==>  finger moved DOWN
    //
    // Why: a mouse wheel rolled "up" generates negative deltaY by browser
    // convention. iOS scroll convention is the opposite: finger-DOWN reveals
    // content above (rubber-band scroll, native Safari/Twitter/Notes behavior).
    // Under the SAME variable definition (deltaY = lastTouchY - currentY),
    // finger-DOWN gives deltaY < 0. So both handlers happen to use
    // "deltaY < 0 means see earlier" — but they get there via different
    // physical conventions. Do NOT collapse the two into a shared sign rule.
    // =====================================================================
    let touchStartY = 0
    let touchStartTime = 0
    let lastTouchY = 0
    let touchVelocity = 0
    let momentumState: MomentumState | null = null
    let eagerEntryTimer: ReturnType<typeof setTimeout> | null = null
    let hadScrollMovement = false

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      // Cancel any running momentum animation
      momentumState?.cancel()
      momentumState = null
      // Cancel any pending eager-entry unwind from a previous touch
      if (eagerEntryTimer) { clearTimeout(eagerEntryTimer); eagerEntryTimer = null }
      touchStartY = e.touches[0].clientY
      lastTouchY = touchStartY
      touchStartTime = performance.now()
      touchVelocity = 0
      hadScrollMovement = false

      // PATH A — Eager copy-mode entry: pre-arm tmux's copy-mode key table on
      // touchstart. By the time the user's finger has crossed the 5px dead-zone
      // (~16-30ms later), tmux has had time to switch key tables and the FIRST
      // touchmove past the dead-zone confirms readiness (see handleTouchMove).
      // Trade-off: a tap-without-scroll briefly enters copy-mode. Unwound by
      // handleTouchEnd's 500ms unwindEagerEntry timer (see below).
      // NOTE: copyModeReady stays false here — flipping it true at touchstart
      // (before tmux acknowledges) caused the first scroll frame to be dropped
      // on real-device delivery latency. The defer-to-deadzone approach gives
      // tmux a real round-trip before scroll keys are flushed.
      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.OPEN && !scrolledUp) {
        sendToWs('\x02[')
        scrolledUp = true
        copyModeReady = false  // confirmed true at first 5px deadzone crossing
        pendingScrollDelta = 0
        scrollOffset = 0
        setIsScrolledUp(true)
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      e.preventDefault() // CRITICAL: requires { passive: false } on listener
      scrollSource = 'touch'

      const currentY = e.touches[0].clientY
      const deltaY = lastTouchY - currentY // see TOUCH SIGN CONVENTION above: deltaY < 0 means finger moved DOWN -> scroll up (view earlier)
      lastTouchY = currentY

      // Track velocity for momentum (pixels per ms)
      const now = performance.now()
      const dt = now - touchStartTime
      if (dt > 0) {
        touchVelocity = deltaY / dt
      }
      touchStartTime = now

      // Dead zone: ignore tiny movements (prevents accidental scroll on tap).
      // Crossing the 5px dead-zone confirms ~16-30ms have elapsed since the
      // eager touchstart \x02[ send, so tmux has had a real round-trip to
      // switch its copy-mode key table. Flip copyModeReady now so subsequent
      // flushes use the fast 30ms gate instead of the conservative 80ms gate.
      if (Math.abs(deltaY) >= 5) {
        hadScrollMovement = true
        if (scrolledUp && !copyModeReady) copyModeReady = true
      }
      if (Math.abs(deltaY) < 5) return

      if (deltaY < 0) {
        // Finger moved DOWN -> scroll UP (view earlier output, iOS convention)
        if (!scrolledUp) {
          sendToWs('\x02[') // Ctrl+B + [ = enter copy-mode
          scrolledUp = true
          copyModeReady = false
          pendingScrollDelta += Math.abs(deltaY)
          scrollOffset += Math.abs(deltaY)
          setIsScrolledUp(true)
          if (scrollTimer) clearTimeout(scrollTimer)
          scrollTimer = setTimeout(() => {
            copyModeReady = true
            flushScrollKeys()
          }, 80)
          return
        }
        pendingScrollDelta += Math.abs(deltaY)
        scrollOffset += Math.abs(deltaY)
      } else if (scrolledUp) {
        // Finger moved UP -> scroll DOWN (return toward live prompt)
        const absDelta = Math.abs(deltaY)
        pendingScrollDelta -= absDelta
        scrollOffset = Math.max(0, scrollOffset - absDelta)
        if (scrollOffset <= 50) {
          if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
          if (pendingScrollDelta !== 0) flushScrollKeys()
          sendToWs('q')
          scrolledUp = false
          copyModeReady = false
          scrollOffset = 0
          pendingScrollDelta = 0
          setIsScrolledUp(false)
          return
        }
      } else {
        // Finger moved UP but not in copy-mode — ignore (no scroll-down past bottom)
        return
      }

      if (scrollTimer) clearTimeout(scrollTimer)
      // 16ms = ~one frame at 60Hz, ~two frames at 120Hz (ProMotion). Tighter
      // gate keeps scroll keys flowing in step with the display refresh, which
      // is what makes the gesture feel "tracked" on newer iPhones. The 80ms
      // pre-warmup gate is preserved while tmux confirms its copy-mode switch.
      scrollTimer = setTimeout(flushScrollKeys, copyModeReady ? 16 : 80)
    }

    const unwindEagerEntry = () => {
      // Fired 500ms after a touch ends with no scroll movement and no momentum.
      // If we entered copy-mode eagerly on touchstart but the user neither
      // scrolled nor typed (which would have exited copy-mode via terminal.onData
      // at lines 812-852), exit copy-mode now so the user is not stranded.
      eagerEntryTimer = null
      if (scrolledUp && pendingScrollDelta === 0 && scrollOffset === 0) {
        sendToWs('q')
        scrolledUp = false
        copyModeReady = false
        setIsScrolledUp(false)
      }
    }

    const handleTouchEnd = () => {
      // Tap-without-scroll: schedule unwind of the eager copy-mode entry.
      // If user neither scrolled nor flung, we exit copy-mode after 500ms so
      // tmux is back in its main key table by the time they decide to type.
      if (!hadScrollMovement && Math.abs(touchVelocity) < 0.3) {
        if (eagerEntryTimer) clearTimeout(eagerEntryTimer)
        eagerEntryTimer = setTimeout(unwindEagerEntry, 500)
        return
      }
      // Start momentum if velocity exceeds threshold (0.3 px/ms)
      if (Math.abs(touchVelocity) < 0.3) return

      momentumState = startMomentum(
        touchVelocity,
        (pixelDelta) => {
          const ws = wsRef.current
          if (!ws || ws.readyState !== WebSocket.OPEN) {
            momentumState?.cancel()
            return
          }
          if (pixelDelta < 0) {
            // Momentum: scroll up (view earlier)
            if (!scrolledUp) {
              sendToWs('\x02[')
              scrolledUp = true
              copyModeReady = false
              pendingScrollDelta += Math.abs(pixelDelta)
              scrollOffset += Math.abs(pixelDelta)
              setIsScrolledUp(true)
              if (scrollTimer) clearTimeout(scrollTimer)
              scrollTimer = setTimeout(() => {
                copyModeReady = true
                flushScrollKeys()
              }, 80)
              return
            }
            pendingScrollDelta += Math.abs(pixelDelta)
            scrollOffset += Math.abs(pixelDelta)
          } else if (scrolledUp) {
            const absDelta = Math.abs(pixelDelta)
            pendingScrollDelta -= absDelta
            scrollOffset = Math.max(0, scrollOffset - absDelta)
            if (scrollOffset <= 50) {
              if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
              if (pendingScrollDelta !== 0) flushScrollKeys()
              sendToWs('q')
              scrolledUp = false
              copyModeReady = false
              scrollOffset = 0
              pendingScrollDelta = 0
              setIsScrolledUp(false)
              momentumState?.cancel()
              return
            }
          } else {
            momentumState?.cancel()
            return
          }
          if (scrollTimer) clearTimeout(scrollTimer)
          // 16ms = ~one frame at 60Hz, ~two frames at 120Hz (ProMotion). Tighter
      // gate keeps scroll keys flowing in step with the display refresh, which
      // is what makes the gesture feel "tracked" on newer iPhones. The 80ms
      // pre-warmup gate is preserved while tmux confirms its copy-mode switch.
      scrollTimer = setTimeout(flushScrollKeys, copyModeReady ? 16 : 80)
        },
        () => { /* onEnd: momentum finished naturally */ },
      )
    }

    // Expose exit-copy-mode function for JSX overlay button
    exitCopyModeRef.current = () => {
      if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
      if (eagerEntryTimer) { clearTimeout(eagerEntryTimer); eagerEntryTimer = null }
      if (pendingScrollDelta !== 0) flushScrollKeys()
      sendToWs('q')
      scrolledUp = false
      copyModeReady = false
      scrollOffset = 0
      pendingScrollDelta = 0
      setIsScrolledUp(false)
      momentumState?.cancel()
    }

    // Register touch event listeners on the outer terminal wrapper.
    //
    // CRITICAL: do NOT attach to .xterm-viewport — in xterm.js v5 it is a SIBLING
    // of .xterm-scrollable-element (both children of .xterm), not an ancestor.
    // Real touches land on the rendered canvas inside .xterm-scrollable-element
    // and bubble up through .xterm → terminalRef.current. They never traverse
    // .xterm-viewport, so a listener there silently never fires on real devices.
    // (Discovered via Playwright instrumentation; the synthetic
    // dispatchEvent(.xterm-viewport) was masking the bug.)
    const scrollTarget = terminalRef.current
    scrollTarget?.addEventListener('touchstart', handleTouchStart, { passive: true })
    scrollTarget?.addEventListener('touchmove', handleTouchMove, { passive: false })
    scrollTarget?.addEventListener('touchend', handleTouchEnd, { passive: true })

    // Expose cleanup to the outer effect — scoped vars above can't be reached from there
    touchCleanup = () => {
      scrollTarget?.removeEventListener('touchstart', handleTouchStart)
      scrollTarget?.removeEventListener('touchmove', handleTouchMove)
      scrollTarget?.removeEventListener('touchend', handleTouchEnd)
      momentumState?.cancel()
      if (eagerEntryTimer) { clearTimeout(eagerEntryTimer); eagerEntryTimer = null }
    }

    // NOTE: No mousedown handler to exit copy-mode. Users need to click-drag
    // to select text from scrollback. Copy-mode exits via:
    // 1. Scrolling all the way back down (handled above)
    // 2. Keyboard input (handled in terminal.onData below)
    // 3. Pressing Escape or q (sent directly to tmux through normal input)

    // Build WebSocket URL with app mount prefix awareness.
    const wsUrl = buildTerminalWebSocketUrl()

    connectStartedAtRef.current = Date.now()

    // ttyd requires the 'tty' subprotocol
    const ws = new WebSocket(wsUrl, ['tty'])
    wsRef.current = ws

    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      if (cancelled || !isPanelActiveRef.current) return
      setStatus('connected')
      reconnectAttemptsRef.current = 0 // Reset on successful connection
      startupRetryAttemptsRef.current = 0
      highBufferCountRef.current = 0
      markInboundActivity()
      markOutboundActivity()
      terminal.focus()

      // Send JSON_DATA init message to spawn the shell
      // ttyd requires this before accepting other commands
      // Message type is implicit - JSON starts with '{'
      if (fit) {
        fit.fit()
        const { cols, rows } = terminal
        const initMsg = JSON.stringify({ columns: cols, rows: rows })
        // Send as UTF-8 encoded JSON (no type prefix needed, '{' is the type)
        ws.send(new TextEncoder().encode(initMsg))
        markOutboundActivity()

        // NOTE: We no longer send cd commands from the frontend.
        // Per-app tmux sessions handle directory and claude automatically:
        // - New session: auto-cd to app dir and start claude
        // - Existing session: just attach (preserves state)

        // Fix for tmux dots: Re-sync terminal size after connection is established
        // This ensures the terminal and tmux session sizes match after the layout
        // has finished rendering and the WebSocket connection is stable
        setTimeout(() => {
          if (fit && ws.readyState === WebSocket.OPEN) {
            fit.fit()
            const { cols, rows } = terminal
            const resizeData = buildResizeFrame(cols, rows)
            ws.send(resizeData)
            markOutboundActivity()
          }
        }, 300) // Delay to let layout stabilize and tmux attach
      }
    }

    ws.onmessage = (event) => {
      if (cancelled || !isPanelActiveRef.current) return
      markInboundActivity()
      if (statusRef.current === 'degraded' || statusRef.current === 'reconnecting') {
        setStatus('connected')
      }
      if (event.data instanceof ArrayBuffer) {
        const data = new Uint8Array(event.data)
        // ttyd protocol: first byte is ASCII message type
        // '0' (48) = output, '1' (49) = window title, '2' (50) = preferences
        const msgType = data[0]
        if (msgType === 48) { // '0' = output
          terminal.write(data.slice(1))
        }
        // Ignore title (49) and preferences (50) messages
      } else if (typeof event.data === 'string') {
        // Handle text frames (first char is message type)
        const msgType = event.data.charCodeAt(0)
        if (msgType === 48) { // '0' = output
          terminal.write(event.data.slice(1))
        }
      }
    }

    ws.onerror = () => {
      if (cancelled || !isPanelActiveRef.current) return
      reportTerminalTelemetry(
        'terminal_ws_error',
        'Terminal WebSocket reported an error.',
        { ready_state: ws.readyState, status: statusRef.current }
      )
      if (statusRef.current === 'connected') {
        setStatus('degraded')
      }
      if (!autoReconnectTimerRef.current) {
        autoReconnectTimerRef.current = setTimeout(() => {
          reconnect({ fromConnectedDrop: true, fromDegradedState: true })
        }, 250)
      }
    }

    ws.onclose = (event) => {
      if (cancelled || !isPanelActiveRef.current) return
      highBufferCountRef.current = 0
      reportTerminalTelemetry(
        'terminal_ws_closed',
        'Terminal WebSocket closed unexpectedly.',
        {
          code: event.code,
          reason: event.reason,
          was_clean: event.wasClean,
          status: statusRef.current,
        }
      )
      // Auto-reconnect when connection drops while connected (e.g., Claude Code exits
      // but tmux session is still alive). Only attempt a few times before showing error.
      const wasSteadyState = statusRef.current === 'connected'
        || statusRef.current === 'degraded'
        || statusRef.current === 'reconnecting'
      if (wasSteadyState && reconnectAttemptsRef.current < MAX_AUTO_RECONNECT_ATTEMPTS) {
        const attempt = reconnectAttemptsRef.current + 1
        reconnectAttemptsRef.current = attempt
        const delayMs = Math.min(5000, Math.round(900 * Math.pow(1.6, attempt - 1)))
        setStatus('reconnecting')
        autoReconnectTimerRef.current = setTimeout(() => {
          reconnect({ fromConnectedDrop: true })
        }, delayMs)
      } else if (
        statusRef.current !== 'connected'
        && startupRetryAttemptsRef.current < MAX_STARTUP_RETRY_ATTEMPTS
      ) {
        startupRetryAttemptsRef.current++
        const ageMs = Date.now() - connectStartedAtRef.current
        if (ageMs > 1200) {
          setStatus('initializing')
        } else {
          setStatus('starting')
        }
        const delayMs = Math.min(5000, 500 + startupRetryAttemptsRef.current * 300)
        autoReconnectTimerRef.current = setTimeout(() => {
          reconnect({ startupRetry: true })
        }, delayMs)
      } else {
        if (statusRef.current === 'connected') {
          terminal.write('\r\n\x1b[31m[Connection closed]\x1b[0m\r\n')
        }
        setStatus('error')
      }
    }

    // Send input to server (uses wsRef.current so reconnect works without re-registering)
    terminal.onData((data) => {
      // If in copy-mode, typing normal characters should exit copy-mode and
      // send the typed character to the shell. Modifier combos (Ctrl+C, Cmd+V,
      // etc.) should NOT exit copy-mode — only plain typing (with optional Shift
      // for capitals). xterm.js onData fires with the character/escape sequence,
      // so we detect modifiers by checking if the data is a control character
      // (< 0x20) or an escape sequence (starts with \x1b).
      if (scrolledUp) {
        const isControlChar = data.length === 1 && data.charCodeAt(0) < 0x20
        const isEscapeSeq = data.startsWith('\x1b')
        const isPlainTyping = !isControlChar && !isEscapeSeq

        if (isPlainTyping) {
          // Exit copy-mode, then send the typed character to the shell
          if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null }
          pendingScrollDelta = 0
          sendToWs('q') // Exit copy-mode in tmux
          scrolledUp = false
          copyModeReady = false
          scrollOffset = 0
          setIsScrolledUp(false)
        }
        // For modifier combos (Ctrl+C, etc.), don't exit copy-mode —
        // let them pass through to tmux copy-mode or be handled normally
      }
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // ttyd protocol: first byte is ASCII '0' (48) for input
        // Use TextEncoder for proper UTF-8 encoding of multi-byte characters (e.g., Cyrillic)
        const encoded = new TextEncoder().encode(data)
        const msg = new Uint8Array(encoded.length + 1)
        msg[0] = 48 // '0' = input type
        msg.set(encoded, 1)
        wsRef.current.send(msg)
        markOutboundActivity()
      } else {
        if (statusRef.current === 'connected' || statusRef.current === 'degraded') {
          setStatus('reconnecting')
        }
        requestRecoverRef.current?.(true)
      }
    })

    // Handle resize (uses wsRef.current so reconnect works without re-registering)
    terminal.onResize(({ cols, rows }) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // ttyd protocol: type '1' (49) for resize
        const data = buildResizeFrame(cols, rows)
        wsRef.current.send(data)
        markOutboundActivity()
      }
    })

      // Resize observer with dimension guard for hidden tabs
      const resizeObserver = new ResizeObserver(() => {
        if (fit && terminalRef.current) {
          const { width, height } = terminalRef.current.getBoundingClientRect()
          // Guard against zero-size containers (hidden tabs, collapsed panels)
          if (width > 0 && height > 0) {
            fit.fit()
          }
        }
      })
      resizeObserver.observe(terminalRef.current!)

      // Store cleanup refs
      terminalInstance.current = terminal
      wsRef.current = ws
    }

    initializeTerminal()

    return () => {
      cancelled = true
      isPanelActiveRef.current = false
      if (autoReconnectTimerRef.current) {
        clearTimeout(autoReconnectTimerRef.current)
        autoReconnectTimerRef.current = null
      }
      // Clean up touch handlers and momentum (assigned inside initializeTerminal)
      touchCleanup?.()
      exitCopyModeRef.current = null
      bellDispose?.()
      webglDispose?.()
      searchAddonRef.current = null
      if (wsRef.current) {
        wsRef.current.onopen = null
        wsRef.current.onmessage = null
        wsRef.current.onerror = null
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
      terminalInstance.current?.dispose()
      terminalInstance.current = null
      fitAddon.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitForServicesReady]) // cwd is intentionally not in deps - we only want to read it once on mount

  // Refit on maximize change
  useEffect(() => {
    if (fitAddon.current) {
      setTimeout(() => fitAddon.current?.fit(), 100)
    }
  }, [isMaximized])

  // Update terminal options when settings change (font size, theme, scrollback, cursor)
  useEffect(() => {
    if (terminalInstance.current && fitAddon.current) {
      terminalInstance.current.options.fontSize = fontSize
      terminalInstance.current.options.theme = getTerminalTheme(terminalTheme)
      terminalInstance.current.options.scrollback = scrollbackLines
      terminalInstance.current.options.cursorStyle = mapCursorStyle(cursorStyle)
      // Clear cached glyph textures so WebGL re-renders with new theme colors
      terminalInstance.current.clearTextureAtlas()
      fitAddon.current.fit()
      // Force xterm viewport background update (CSS default is #000)
      const vp = terminalRef.current?.querySelector('.xterm-viewport') as HTMLElement | null
      if (vp) {
        vp.style.backgroundColor = getTerminalTheme(terminalTheme).background ?? ''
      }
    }
  }, [fontSize, terminalTheme, scrollbackLines, cursorStyle])

  const reconnect = useCallback((options?: ReconnectOptions) => {
    if (!isPanelActiveRef.current) return

    // Clear any pending auto-reconnect timer
    if (autoReconnectTimerRef.current) {
      clearTimeout(autoReconnectTimerRef.current)
      autoReconnectTimerRef.current = null
    }

    // Close existing WebSocket if any
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onmessage = null
      wsRef.current.onerror = null
      wsRef.current.onclose = null // Prevent triggering error state
      wsRef.current.close()
      wsRef.current = null
    }

    if (!options?.startupRetry && !options?.fromConnectedDrop) {
      // Manual reconnect should start with a clean retry budget.
      reconnectAttemptsRef.current = 0
      startupRetryAttemptsRef.current = 0
    }

    connectStartedAtRef.current = Date.now()
    if (options?.startupRetry) {
      setStatus('initializing')
    } else if (options?.fromConnectedDrop || options?.fromDegradedState) {
      setStatus('reconnecting')
    } else {
      setStatus('connecting')
    }

    const terminal = terminalInstance.current
    if (!terminal) {
      setStatus('error')
      return
    }

    if (!options?.startupRetry && !options?.fromConnectedDrop) {
      // Manual reconnect starts from a clean terminal surface.
      terminal.clear()
      terminal.write('\r\n\x1b[33m[Reconnecting...]\x1b[0m\r\n')
    } else if (options?.fromConnectedDrop) {
      terminal.write('\r\n\x1b[33m[Reconnecting...]\x1b[0m\r\n')
    }

    // Build WebSocket URL (same logic as initial connection).
    const wsUrl = buildTerminalWebSocketUrl()

    const ws = new WebSocket(wsUrl, ['tty'])
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    ws.onopen = () => {
      if (!isPanelActiveRef.current) return
      setStatus('connected')
      reconnectAttemptsRef.current = 0 // Reset on successful connection
      startupRetryAttemptsRef.current = 0
      highBufferCountRef.current = 0
      markInboundActivity()
      markOutboundActivity()
      terminal.focus()
      if (fitAddon.current) {
        fitAddon.current.fit()
        const { cols, rows } = terminal
        const initMsg = JSON.stringify({ columns: cols, rows: rows })
        ws.send(new TextEncoder().encode(initMsg))
        markOutboundActivity()
        // Re-sync size after layout stabilizes
        setTimeout(() => {
          if (fitAddon.current && ws.readyState === WebSocket.OPEN) {
            fitAddon.current.fit()
            const { cols, rows } = terminal
            const resizeData = buildResizeFrame(cols, rows)
            ws.send(resizeData)
            markOutboundActivity()
          }
        }, 300)
      }
    }

    ws.onmessage = (event) => {
      if (!isPanelActiveRef.current) return
      markInboundActivity()
      if (statusRef.current === 'degraded' || statusRef.current === 'reconnecting') {
        setStatus('connected')
      }
      if (event.data instanceof ArrayBuffer) {
        const data = new Uint8Array(event.data)
        const msgType = data[0]
        if (msgType === 48) terminal.write(data.slice(1))
      } else if (typeof event.data === 'string') {
        const msgType = event.data.charCodeAt(0)
        if (msgType === 48) terminal.write(event.data.slice(1))
      }
    }

    ws.onerror = () => {
      if (!isPanelActiveRef.current) return
      reportTerminalTelemetry(
        'terminal_ws_error',
        'Terminal WebSocket reported an error.',
        { ready_state: ws.readyState, status: statusRef.current }
      )
      if (statusRef.current === 'connected') {
        setStatus('degraded')
      }
      if (!autoReconnectTimerRef.current) {
        autoReconnectTimerRef.current = setTimeout(() => {
          reconnect({ fromConnectedDrop: true, fromDegradedState: true })
        }, 250)
      }
    }

    ws.onclose = (event) => {
      if (!isPanelActiveRef.current) return
      highBufferCountRef.current = 0
      reportTerminalTelemetry(
        'terminal_ws_closed',
        'Terminal WebSocket closed unexpectedly.',
        {
          code: event.code,
          reason: event.reason,
          was_clean: event.wasClean,
          status: statusRef.current,
        }
      )
      const wasSteadyState = statusRef.current === 'connected'
        || statusRef.current === 'degraded'
        || statusRef.current === 'reconnecting'
      if (wasSteadyState && reconnectAttemptsRef.current < MAX_AUTO_RECONNECT_ATTEMPTS) {
        const attempt = reconnectAttemptsRef.current + 1
        reconnectAttemptsRef.current = attempt
        const delayMs = Math.min(5000, Math.round(900 * Math.pow(1.6, attempt - 1)))
        setStatus('reconnecting')
        autoReconnectTimerRef.current = setTimeout(() => {
          reconnect({ fromConnectedDrop: true })
        }, delayMs)
      } else if (
        statusRef.current !== 'connected'
        && startupRetryAttemptsRef.current < MAX_STARTUP_RETRY_ATTEMPTS
      ) {
        startupRetryAttemptsRef.current++
        const ageMs = Date.now() - connectStartedAtRef.current
        if (ageMs > 1200) {
          setStatus('initializing')
        } else {
          setStatus('starting')
        }
        const delayMs = Math.min(5000, 500 + startupRetryAttemptsRef.current * 300)
        autoReconnectTimerRef.current = setTimeout(() => {
          reconnect({ startupRetry: true })
        }, delayMs)
      } else {
        if (statusRef.current === 'connected') {
          terminal.write('\r\n\x1b[31m[Connection closed]\x1b[0m\r\n')
        }
        setStatus('error')
      }
    }

    // Note: terminal.onData and terminal.onResize handlers already reference
    // wsRef.current (the ref, not the value), so they automatically use the
    // new WebSocket after reconnect. No need to re-register them.
  }, [
    buildResizeFrame,
    buildTerminalWebSocketUrl,
    markInboundActivity,
    markOutboundActivity,
    reportTerminalTelemetry,
  ])

  useEffect(() => {
    requestRecoverRef.current = (fromDegraded?: boolean) => {
      if (autoReconnectTimerRef.current) return
      autoReconnectTimerRef.current = setTimeout(() => {
        reconnect({ fromConnectedDrop: true, fromDegradedState: fromDegraded })
      }, 150)
    }
    return () => {
      requestRecoverRef.current = null
    }
  }, [reconnect])

  useEffect(() => {
    const maybeRecoverAfterVisibility = (source: 'visibility' | 'focus' | 'pageshow') => {
      if (!isPanelActiveRef.current) return
      const ws = wsRef.current
      const now = Date.now()
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        if (statusRef.current !== 'checking' && statusRef.current !== 'starting' && statusRef.current !== 'initializing') {
          reconnect({ fromConnectedDrop: true, fromDegradedState: true })
        }
        return
      }

      const hiddenFor = hiddenSinceRef.current ? now - hiddenSinceRef.current : 0
      const isVisibilityWake = source === 'visibility' && hiddenFor >= VISIBILITY_RECOVER_AFTER_MS
      const isFocusWake = source === 'focus' && hiddenFor >= VISIBILITY_RECOVER_AFTER_MS
      if ((isVisibilityWake || isFocusWake) && now - lastVisibilityRecoverAtRef.current > VISIBILITY_RECOVER_COOLDOWN_MS) {
        lastVisibilityRecoverAtRef.current = now
        setStatus('reconnecting')
        reconnect({ fromConnectedDrop: true, fromDegradedState: true })
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now()
        return
      }
      maybeRecoverAfterVisibility('visibility')
      hiddenSinceRef.current = null
    }

    const handleFocus = () => {
      maybeRecoverAfterVisibility('focus')
    }

    const handlePageShow = () => {
      maybeRecoverAfterVisibility('pageshow')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('pageshow', handlePageShow)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [VISIBILITY_RECOVER_AFTER_MS, VISIBILITY_RECOVER_COOLDOWN_MS, reconnect])

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPanelActiveRef.current) return
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      if (ws.bufferedAmount >= WS_HIGH_BUFFER_BYTES) {
        highBufferCountRef.current += 1
        if (highBufferCountRef.current >= WS_HIGH_BUFFER_STRIKES) {
          if (statusRef.current === 'connected') {
            setStatus('degraded')
          }
          reportTerminalTelemetry(
            'terminal_ws_buffer_stall',
            'Terminal WebSocket buffered output stalled and forced recovery.',
            {
              buffered_amount: ws.bufferedAmount,
              strikes: highBufferCountRef.current,
            }
          )
          reconnect({ fromConnectedDrop: true, fromDegradedState: true })
          return
        }
      } else {
        highBufferCountRef.current = 0
      }

      const now = Date.now()
      const mostRecentActivity = Math.max(lastInboundMessageAtRef.current, lastOutboundMessageAtRef.current)
      if (now - mostRecentActivity < WS_KEEPALIVE_INTERVAL_MS) return

      const terminal = terminalInstance.current
      if (!terminal) return

      try {
        const data = buildResizeFrame(terminal.cols, terminal.rows)
        ws.send(data)
        markOutboundActivity()
      } catch {
        if (statusRef.current === 'connected') {
          setStatus('degraded')
        }
        reconnect({ fromConnectedDrop: true, fromDegradedState: true })
      }
    }, WS_HEALTH_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [
    WS_HEALTH_INTERVAL_MS,
    WS_HIGH_BUFFER_BYTES,
    WS_HIGH_BUFFER_STRIKES,
    WS_KEEPALIVE_INTERVAL_MS,
    buildResizeFrame,
    markOutboundActivity,
    reconnect,
    reportTerminalTelemetry,
  ])

  return (
    <Panel
      title="Terminal"
      icon={<TerminalIcon className="w-4 h-4" />}
      isMaximized={isMaximized}
      onMaximize={onMaximize}
      onClose={onClose}
      closeable={closeable}
      hideToolbar
    >
      <div className="relative h-full" style={{ backgroundColor: getBackgroundColor(terminalTheme) }}>
        {(status === 'checking' || status === 'starting' || status === 'initializing' || status === 'connecting') && (
          <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: getBackgroundColor(terminalTheme) }}>
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">{statusMessages[status]}</span>
          </div>
        )}
        {(status === 'reconnecting' || status === 'degraded') && (
          <div className="absolute top-3 right-3 z-10 rounded-md border border-border bg-background/90 px-2.5 py-1.5 text-xs flex items-center gap-1.5 shadow-sm">
            {status === 'reconnecting' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
            ) : (
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
            )}
            <span className="text-muted-foreground">
              {status === 'reconnecting' ? 'Reconnecting...' : 'No terminal activity, recovering...'}
            </span>
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10" style={{ backgroundColor: getBackgroundColor(terminalTheme) }}>
            <AlertCircle className="w-8 h-8 text-destructive mb-2" />
            <span className="text-muted-foreground mb-4">Connection failed</span>
            <button
              onClick={() => reconnect()}
              className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reconnect
            </button>
          </div>
        )}
        {showSearch && searchAddonRef.current && (
          <TerminalSearchBar
            searchAddon={searchAddonRef.current}
            onClose={() => {
              setShowSearch(false)
              terminalInstance.current?.focus()
            }}
          />
        )}
        <div ref={terminalRef} className="h-full p-2" />
      </div>
    </Panel>
  )
}
